import { localeCompare } from 'common-utils/string-utils';
import {
    DeploymentDetails,
    DeploymentDetailsDeviceDto,
    DeploymentDetailsTargetDto,
} from 'dto/deployment/deployment-details.dto';
import { PROGRESS_STAGE, PROGRESS_STATUS } from 'dto/deployment/deployment-dto';
import { DEPLOYMENT_TARGET_STATUS } from 'dto/deployment/target-dto';
import { DEVICE_JOB_EXECUTION_STATUS } from 'dto/remote-commands';
import {
    IDeploymentDetailsDeviceRecord,
    IDeploymentDeviceGroupsBuildResult,
    IDeploymentExportRecord,
} from './list.types';

type DeploymentProgressIconName = 'xmark-circle' | 'pause-circle' | 'play-circle' | 'check-circle';

export class DeploymentListingHelpers {
    private static readonly progressBarPercent: Record<string, number> = {
        [PROGRESS_STAGE.QUEUE]: 10,
        [PROGRESS_STAGE.IN_PROGRESS]: 50,
        [PROGRESS_STAGE.DOWNLOADED]: 70,
        [PROGRESS_STAGE.SUCCEEDED]: 100,
        [PROGRESS_STAGE.FAILED]: 100,
    };

    private static readonly progressIconName: Record<string, DeploymentProgressIconName> = {
        [PROGRESS_STATUS.ERROR]: 'xmark-circle',
        [PROGRESS_STATUS.PAUSE]: 'pause-circle',
        [PROGRESS_STATUS.PLAY]: 'play-circle',
        [PROGRESS_STATUS.SUCCESS]: 'check-circle',
    };

    public static buildProgress(device: DeploymentDetailsDeviceDto): IDeploymentDetailsDeviceRecord['progress'] {
        const status = device.executionStatus;
        if (!status) return undefined;

        switch (status) {
            case DEVICE_JOB_EXECUTION_STATUS.QUEUED:
                return {
                    status: PROGRESS_STATUS.PAUSE,
                    stage: PROGRESS_STAGE.QUEUE,
                };
            case DEVICE_JOB_EXECUTION_STATUS.IN_PROGRESS:
                return {
                    status: device.connectivity?.connected
                          ? PROGRESS_STATUS.PLAY
                          : PROGRESS_STATUS.PAUSE,
                    stage: PROGRESS_STAGE.IN_PROGRESS,
                };
            case DEVICE_JOB_EXECUTION_STATUS.SUCCEEDED:
                return {
                    status: PROGRESS_STATUS.SUCCESS,
                    stage: PROGRESS_STAGE.SUCCEEDED,
                };
            default:
                return {
                    status: PROGRESS_STATUS.ERROR,
                    stage: PROGRESS_STAGE.FAILED,
                };
        }
    }

    public static buildDeviceGroupDetails(targets: DeploymentDetailsTargetDto[]): IDeploymentDeviceGroupsBuildResult {
        let devicesWithoutConnectivityCount = 0;
        const deviceGroups = targets.flatMap((target) =>
            target.deviceGroups.map((deviceGroup) => {
                const devices = deviceGroup.devices.map((device) => ({
                    deviceName: device.deviceName,
                    assignedToGroupAt: device.assignedToGroupAt,
                    connectivity: device.connectivity,
                    executionStatus: device.executionStatus,
                    progress: DeploymentListingHelpers.buildProgress(device)
                }));

                devicesWithoutConnectivityCount += devices.filter((device) => !device.connectivity).length;

                return {
                    name: deviceGroup.name,
                    label: deviceGroup.label,
                    timezone: deviceGroup.timezone,
                    downloadOnly: target.downloadOnly,
                    installDateTime: target.installDateTime,
                    scheduleDateTime: target.scheduleDateTime,
                    devices,
                    numberOfDeployedDevices: devices.filter(
                        (device) => device.executionStatus === DEVICE_JOB_EXECUTION_STATUS.SUCCEEDED,
                    ).length,
                };
            }),
        );

        return { deviceGroups, devicesWithoutConnectivityCount };
    }

    public static partitionDevicesByConnectivity(devices: IDeploymentDetailsDeviceRecord[]): {
        online: IDeploymentDetailsDeviceRecord[];
        offline: IDeploymentDetailsDeviceRecord[];
    } {
        return devices.reduce(
            (result, device) => {
                result[device.connectivity?.connected ? 'online' : 'offline'].push(device);
                return result;
            },
            { online: [], offline: [] } as {
                online: IDeploymentDetailsDeviceRecord[];
                offline: IDeploymentDetailsDeviceRecord[];
            },
        );
    }

    public static sortDevices(
        previous: IDeploymentDetailsDeviceRecord,
        current: IDeploymentDetailsDeviceRecord,
    ): number {
        if (previous.assignedToGroupAt && current.assignedToGroupAt) {
            return previous.assignedToGroupAt > current.assignedToGroupAt
                ? -1
                : previous.assignedToGroupAt < current.assignedToGroupAt
                  ? 1
                  : 0;
        }
        if (!previous.assignedToGroupAt && !current.assignedToGroupAt) {
            return localeCompare(current.deviceName, previous.deviceName);
        }
        return previous.assignedToGroupAt ? -1 : current.assignedToGroupAt ? 1 : 0;
    }

    public static compareProgressStatus(
        previous: IDeploymentDetailsDeviceRecord,
        current: IDeploymentDetailsDeviceRecord,
    ): number {
        const previousStage = previous.progress?.stage;
        const currentStage = current.progress?.stage;

        if (!currentStage || !previousStage) {
            return currentStage === previousStage ? 0 : !currentStage ? -1 : 1;
        }

        return currentStage === PROGRESS_STAGE.SUCCEEDED
            ? 1
            : currentStage === PROGRESS_STAGE.FAILED
              ? -1
              : previousStage === PROGRESS_STAGE.SUCCEEDED
                ? -1
                : previousStage === PROGRESS_STAGE.FAILED
                  ? 1
                  : 0;
    }

    public static matchProgressFilter(value: string, device: IDeploymentDetailsDeviceRecord): boolean {
        const stage = device.progress?.stage;
        if (
            value === PROGRESS_STAGE.IN_PROGRESS &&
            [PROGRESS_STAGE.QUEUE, PROGRESS_STAGE.IN_PROGRESS, PROGRESS_STAGE.DOWNLOADED].includes(stage)
        ) {
            return true;
        }
        return value === stage;
    }

    public static isErrorProgressStatus(status: PROGRESS_STATUS): boolean {
        return status === PROGRESS_STATUS.ERROR || status === PROGRESS_STATUS.PAUSE;
    }

    public static getProgressBarPercent(stage: PROGRESS_STAGE): number {
        return DeploymentListingHelpers.progressBarPercent[stage];
    }

    public static getProgressIconName(status: PROGRESS_STATUS): DeploymentProgressIconName {
        return DeploymentListingHelpers.progressIconName[status];
    }

    public static getProgressBarColor(status: PROGRESS_STATUS, stage: PROGRESS_STAGE): string {
        return status === PROGRESS_STATUS.PLAY || stage === PROGRESS_STAGE.SUCCEEDED ? '#52C41A' : '#D61346';
    }

    public static getExportRows(
        deploymentDetails: DeploymentDetails,
        deploymentGroupLabel = '',
    ): IDeploymentExportRecord[] {
        return deploymentDetails.deviceGroups.flatMap((group) =>
            group.devices.map((device) => ({
                uid: device.deviceName,
                serialNumber: device.serialNumber,
                esn: device.esn,
                firmwareVersion: device.firmwareVersion,
                softwareUpdateStatus: device.executionStatus,
                deviceGroup: group.label,
                deploymentGroup: deploymentGroupLabel,
                deploymentName: deploymentDetails.name,
                deploymentUnits: deploymentDetails.units.map((item) => item.displayName).join(', '),
            })),
        );
    }
}
